import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, 
  Button, 
  Typography, 
  Paper, 
  Container, 
  Grid,
  Divider,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  LinearProgress,
  Pagination,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  Select,
  FormControl,
  InputLabel,
  CircularProgress
} from '@mui/material';
import {
  Add as AddIcon, 
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Inventory as InventoryIcon,
  FilterList as FilterIcon,
  GridView as GridViewIcon,
  List as ListViewIcon,
  CategoryOutlined as CategoryIcon,
  ChevronRight as ChevronRightIcon,
  ArrowBack as ArrowBackIcon,
  FolderOpen as FolderOpenIcon,
  ExpandMore as ExpandMoreIcon,
  FileDownload as FileDownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon
} from '@mui/icons-material';
import Layout from '../../components/layout/Layout';
import ProductForm from '../../components/products/ProductForm';
import productService from '../../services/productService';
import { useSnackbar } from 'notistack';
import categoryService from '../../services/categoryService';
import { getCurrentUser } from '../../services/authService';
import axios from 'axios';
import { getAuthHeader } from '../../services/authService';
import API_CONFIG from '../../config';
import * as XLSX from 'xlsx';

// Mock data as a last resort fallback
const MOCK_PRODUCTS = [
  {
    id: 1,
    name: 'Product 1',
    category: { id: 1, name: 'Category 1' },
    sellingPrice: 19.99,
    quantity: 50,
    unit: { abbreviation: 'Pcs' },
    status: 'ACTIVE'
  },
  {
    id: 2,
    name: 'Product 2',
    category: { id: 2, name: 'Category 2' },
    sellingPrice: 29.99,
    quantity: 30,
    unit: { abbreviation: 'Pcs' },
    status: 'ACTIVE'
  },
  {
    id: 3,
    name: 'Product 3',
    category: { id: 1, name: 'Category 1' },
    sellingPrice: 9.99,
    quantity: 10,
    unit: { abbreviation: 'Pcs' },
    status: 'INACTIVE'
  }
];

const ProductsPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const user = getCurrentUser();
  const companyId = user?.companyId || 1; // Fallback to 1 for development
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);
  const [dialogMode, setDialogMode] = useState('create'); // 'create', 'edit', or 'view'
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [activeTabIndex, setActiveTabIndex] = useState(0); // Track active tab
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 12
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [categories, setCategories] = useState([]);
  const [currentCategory, setCurrentCategory] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'All Products' }]);
  const [categoryTree, setCategoryTree] = useState({ rootCategories: [], subcategories: {} });
  const [originalCategories, setOriginalCategories] = useState([]);
  const [mainCategories, setMainCategories] = useState([]); // For tab navigation
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [categoryDropdownAnchorEl, setCategoryDropdownAnchorEl] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  // Get the menu state for each product
  const isMenuOpen = Boolean(anchorEl);

  // Fetch main categories for tab navigation
  const fetchMainCategories = useCallback(async () => {
    try {
      const data = await categoryService.getRootCategories(companyId);
      setMainCategories(data);
    } catch (error) {
      console.error('Error fetching main categories:', error);
      enqueueSnackbar('Failed to load category navigation', { variant: 'error' });
    }
  }, [companyId, enqueueSnackbar]);

  // Initial load - fetch main categories for tabs
  useEffect(() => {
    fetchMainCategories();
  }, [fetchMainCategories]);

  // Memoize fetchProducts to prevent unnecessary re-renders
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      // If a category is selected, fetch products for that category
      if (currentCategory) {
        const data = await productService.getProductsByCategoryForManagement(
          currentCategory.id,
        pagination.page,
        pagination.size,
        'name',
        'asc'
      );
        console.log('Fetched products for category:', currentCategory.name, data.content); // Debug log
        setProducts(data.content || data);
        setFilteredProducts(data.content || data);
      setPagination(prev => ({
        ...prev,
          totalPages: data.totalPages || 1,
          totalElements: data.totalElements || (data.content ? data.content.length : data.length)
      }));
      } else {
        // No category selected (ALL tab), so fetch all products
        const data = await productService.getAllProductsForManagement(
          pagination.page,
          pagination.size,
          'name',
          'asc'
        );
        console.log('Fetched all products:', data.content); // Debug log
        setProducts(data.content || data);
        setFilteredProducts(data.content || data);
        setPagination(prev => ({
          ...prev,
          totalPages: data.totalPages || 1,
          totalElements: data.totalElements || (data.content ? data.content.length : data.length)
        }));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      enqueueSnackbar('Failed to load products', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, currentCategory, enqueueSnackbar]);

  // Fetch categories and build the category tree
  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      let categoryData;
      
      if (currentCategory) {
        // Fetch subcategories for the current category
        categoryData = await categoryService.getSubcategories(currentCategory.id);
      } else {
        // Fetch root categories (no parent)
        categoryData = await categoryService.getRootCategories(companyId);
      }
      
      setCategories(categoryData);
      
      // Store original categories for filtering
      if (!currentCategory) {
        setOriginalCategories(categoryData);
      }
      
      // Always fetch the full category tree if we don't have it yet
      if (Object.keys(categoryTree.subcategories).length === 0) {
        const allCategories = await categoryService.getCategoriesByCompany(companyId);
        
        // Build category tree
        const tree = categoryService.buildCategoryTree({
          content: allCategories
        });
        
        setCategoryTree(tree);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      enqueueSnackbar('Failed to load categories', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [currentCategory, companyId, enqueueSnackbar, categoryTree.subcategories]);

  // Initial load - only fetch categories when the component mounts or currentCategory changes
  useEffect(() => {
    fetchCategories();
  }, [currentCategory, fetchCategories]);

  // Load products when a category is selected
  useEffect(() => {
    if (currentCategory) {
      fetchProducts();
    }
  }, [currentCategory, fetchProducts]);

  // Apply search filter if needed (Only for categories now)
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();

      // Only filter categories client-side if no category is selected
      if (!currentCategory) {
        if (originalCategories.length > 0) {
          const filtered = originalCategories.filter(category =>
            category.name.toLowerCase().includes(query) ||
            (category.description && category.description.toLowerCase().includes(query))
          );
          setCategories(filtered);
        }
      }
      // --- Removed client-side product filtering logic ---
    } else {
      // Reset filters when search query is empty
      if (currentCategory) {
        // Product list will be reset by fetchTabProducts called in handleSearch
      } else {
        // Use original categories instead of refetching
        setCategories(originalCategories);
      }
    }
  }, [originalCategories, searchQuery, currentCategory]); // Removed 'products' dependency

  // Handle navigating to a category
  const handleNavigateToCategory = (category) => {
    // Set the current category
    setCurrentCategory(category);
    
    // Update breadcrumbs
    setBreadcrumbs([...breadcrumbs, { id: category.id, name: category.name }]);
    
    // Reset pagination when navigating
    setPagination(prev => ({
      ...prev,
      page: 0
    }));
    
    // If navigating to a root category, update the tab index
    if (breadcrumbs.length === 1) { // Only 'All Products' in breadcrumbs
    const rootIndex = mainCategories.findIndex(cat => cat.id === category.id);
    if (rootIndex !== -1) {
      setActiveTabIndex(rootIndex + 1); // +1 because "ALL" is index 0
    }
    }
    
    // Always fetch products for this category
    fetchTabProducts(category.id);
  };

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = (breadcrumb, index) => {
    if (index === 0) {
      // Back to root level
      setCurrentCategory(null);
      setBreadcrumbs([{ id: null, name: 'All Products' }]);
      setActiveTabIndex(0); // Set to "ALL" tab
      // Fetch all products
      fetchTabProducts();
    } else {
      // Navigate to specific breadcrumb
      const category = breadcrumbs[index];
      
      // If we're navigating to a parent category from a subcategory
      if (index < breadcrumbs.length - 1) {
        // Need to truncate breadcrumbs to remove any further subcategories
      setBreadcrumbs(breadcrumbs.slice(0, index + 1));
      }
      
      // Set category and update UI
      setCurrentCategory({ id: category.id, name: category.name });
      
      // If navigating to a root category, update the tab index
      if (index === 1) {
        const rootIndex = mainCategories.findIndex(cat => cat.id === category.id);
        if (rootIndex !== -1) {
          setActiveTabIndex(rootIndex + 1); // +1 because "ALL" is index 0
        }
      }
      
      // Always fetch products for this category
      fetchTabProducts(category.id);
    }
    
    // Reset pagination when navigating
    setPagination(prev => ({
      ...prev,
      page: 0
    }));
  };

  const handleOpenDialog = (mode, product = null) => {
    setDialogMode(mode);
    setCurrentProduct(product);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentProduct(null);
  };

  const handleMenuOpen = (event, productId) => {
    setAnchorEl(event.currentTarget);
    setSelectedProductId(productId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedProductId(null);
  };

  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      page: value - 1 // MUI Pagination is 1-based, but our API is 0-based
    });
  };

  const handleProductSubmit = async (formData) => {
    try {
      console.log('Submitting product data:', formData); // Debug log
      if (dialogMode === 'create') {
        await productService.createProduct(formData);
        enqueueSnackbar('Product created successfully', { variant: 'success' });
      } else if (dialogMode === 'edit' && currentProduct) {
        console.log('Updating product with ID:', currentProduct.id, 'Data:', formData); // Debug log
        await productService.updateProduct(currentProduct.id, formData);
        enqueueSnackbar('Product updated successfully', { variant: 'success' });
      }
      
      handleCloseDialog();
      fetchTabProducts();
    } catch (error) {
      console.error('Error submitting product:', error);
      console.error('Error response:', error.response?.data); // Debug log
      enqueueSnackbar('Failed to save product', { variant: 'error' });
    }
  };

  // Memoize the product card render function
  const renderProductCard = useCallback((product) => {
    const imageUrl = product.imageUrl ? 
      productService.getProductImageUrl(product.imageUrl) : 
      '/placeholder-product.png';

    console.log('Rendering product:', product.name, 'Status:', product.status); // Debug log
    const isInactive = String(product.status || 'ACTIVE').toUpperCase() === 'INACTIVE';

    return (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={product.id}>
        <Card sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: 'column', 
          transition: 'transform 0.2s',
          opacity: isInactive ? 0.7 : 1,
          filter: isInactive ? 'grayscale(50%)' : 'none',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: 4
          },
          maxWidth: '100%',
          position: 'relative',
          border: '1px solid #e0e0e0',
          borderRadius: 1,
        }}>
          <Box sx={{ 
            position: 'relative', 
            paddingTop: '100%', // Square aspect ratio for product images
            width: '100%',
            bgcolor: 'white',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <CardMedia
              component="img"
              image={imageUrl}
              alt={product.name}
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain', 
                p: 2, 
                filter: isInactive ? 'grayscale(50%)' : 'none'
              }}
              loading="lazy"
            />
            <IconButton 
              size="small" 
              onClick={(e) => handleMenuOpen(e, product.id)}
              sx={{
                position: 'absolute',
                top: 5,
                right: 5,
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
          </Box>
          <CardContent sx={{ flexGrow: 1, p: 2, pt: 1.5, pb: '16px !important' }}>
            <Typography 
              variant="subtitle1" 
              component="h2" 
              noWrap
              sx={{ 
                color: isInactive ? 'text.disabled' : 'text.primary',
                fontWeight: 'medium',
                mb: 0.5
              }}
            >
              {product.name}
            </Typography>
            <Typography 
              color={isInactive ? 'text.disabled' : 'text.secondary'} 
              variant="body2" 
              sx={{ mb: 1.5 }}
            >
              {product.category?.name || 'Uncategorized'}
            </Typography>
          
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5 
            }}>
              <Typography variant="caption" color="text.secondary">
                Sale Price
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Quantity
              </Typography>
            </Box>
            
            <Box sx={{ 
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 0.5 
            }}>
              <Typography 
                variant="body2" 
                fontWeight="bold" 
                color={isInactive ? 'text.disabled' : 'primary.main'}
              >
                ${parseFloat(product.sellingPrice).toFixed(2)}
              </Typography>
              <Typography 
                variant="body2" 
                color={isInactive ? 'text.disabled' : 'text.primary'}
              >
                {product.quantity} {product.unit?.abbreviation || 'Pcs'}
              </Typography>
            </Box>
            
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <Chip 
                label={isInactive ? 'Inactive' : 'Active'}
                size="small"
                sx={{
                  height: '22px',
                  fontWeight: 'medium',
                  backgroundColor: isInactive ? '#e0e0e0' : '#e8f5e9',
                  color: isInactive ? '#757575' : '#2e7d32',
                  border: isInactive ? '1px solid #bdbdbd' : '1px solid #a5d6a7',
                  '& .MuiChip-label': {
                    px: 1,
                    py: 0
                  }
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </Grid>
    );
  }, [handleMenuOpen]);

  // Render category card for displaying categories
  const renderCategoryCard = useCallback((category) => {
    // Get image URL for category
    const categoryImage = category.imageUrl ? 
      categoryService.getCategoryImageUrl(category.imageUrl) : 
      '/placeholder-category.png';
    
    // Check if this category has subcategories
    const hasSubcategories = categoryTree.subcategories[category.id]?.length > 0;
    
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} xl={2} key={category.id}>
        <Card 
          onClick={() => handleNavigateToCategory(category)}
          sx={{ 
            height: '100%', 
            display: 'flex', 
            flexDirection: 'column', 
            cursor: 'pointer',
            transition: 'transform 0.2s',
            '&:hover': {
              transform: 'translateY(-4px)',
              boxShadow: 4
            },
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            backgroundColor: 'rgba(25, 118, 210, 0.04)', // Light blue background for subcategories
            borderLeft: '4px solid #1976d2', // Left border indicator
          }}
        >
          <Box sx={{ 
            position: 'relative', 
            paddingTop: '100%', // Square aspect ratio 
            width: '100%',
            bgcolor: 'white',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <CardMedia
              component="img"
              image={categoryImage}
              alt={category.name}
              sx={{ 
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain', 
                p: 2
              }}
              loading="lazy"
            />
          </Box>
          <CardContent sx={{ flexGrow: 1, p: 2, pt: 1.5, pb: '16px !important' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
              <CategoryIcon sx={{ mr: 1, color: 'primary.main', fontSize: '1rem' }} />
              <Typography 
                variant="subtitle1" 
                component="h2"
                noWrap
                sx={{ fontWeight: 'medium' }}
              >
                {category.name}
              </Typography>
            </Box>
            
            {category.description && (
              <Typography 
                color="text.secondary" 
                variant="body2" 
                sx={{ 
                  mb: 1.5,
                  display: '-webkit-box',
                  overflow: 'hidden',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                  height: '40px'
                }}
              >
                {category.description}
              </Typography>
            )}
            
            {hasSubcategories && (
              <Chip 
                icon={<FolderOpenIcon fontSize="small" />}
                label={`${categoryTree.subcategories[category.id].length} Subcategories`}
                size="small"
                color="primary"
                variant="outlined"
                sx={{ 
                  position: 'absolute',
                  bottom: 16,
                  right: 16,
                  height: '24px'
                }}
              />
            )}
          </CardContent>
        </Card>
      </Grid>
    );
  }, [categoryTree, handleNavigateToCategory]);

  // Memoize the product/category grid
  const contentGrid = useMemo(() => {
    if (loading) {
      return (
        <Grid item xs={12}>
          <LinearProgress />
        </Grid>
      );
    }
    
    // If a category is selected
    if (currentCategory) {
      const gridItems = [];
      
      // First, add subcategories if available
      const hasSubcategories = categories.length > 0;
      if (hasSubcategories) {
        // Add a section header for subcategories
        gridItems.push(
          <Grid item xs={12} key="subcategories-header">
            <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
              <FolderOpenIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h2" color="primary.main">
                Subcategories
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>
        );
        
        // Add subcategories
        categories.forEach(category => {
          gridItems.push(renderCategoryCard(category));
        });
      }
      
      // Then add products if available
      if (filteredProducts.length > 0) {
        // Add a section header for products
        gridItems.push(
          <Grid item xs={12} key="products-header">
            <Box sx={{ mt: 3, mb: 1, display: 'flex', alignItems: 'center' }}>
              <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" component="h2" color="primary.main">
                Products
              </Typography>
            </Box>
            <Divider sx={{ mb: 2 }} />
          </Grid>
        );
        
        if (viewMode === 'grid') {
          filteredProducts.forEach(product => {
            gridItems.push(renderProductCard(product));
          });
        } else {
          // For list view, add a table with products
          gridItems.push(
            <Grid item xs={12} key="product-list">
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Sale Price</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                    {filteredProducts.map(product => (
                      <TableRow key={product.id}>
                        <TableCell>
                          <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {product.imageUrl ? (
                              <CardMedia
                            component="img" 
                                image={productService.getProductImageUrl(product.imageUrl)}
                            alt={product.name}
                                sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <InventoryIcon color="action" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>{product.name}</TableCell>
                        <TableCell>{product.category?.name || 'Uncategorized'}</TableCell>
                        <TableCell align="right">{product.sellingPrice?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell align="right">{product.quantity || 0} {product.unit?.abbreviation || ''}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={product.status || 'ACTIVE'} 
                            color={product.status === 'ACTIVE' ? 'success' : 'default'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <IconButton onClick={(e) => handleMenuOpen(e, product.id)} size="small">
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        );
        }
      }
      
      // If we have either subcategories or products, return them
      if (gridItems.length > 0) {
        return gridItems;
      }
      
      // No content found
        return (
          <Grid item xs={12}>
          <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h6" color="text.secondary">
              No content found
              </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Add a product to this category to get started
              </Typography>
                <Button 
                  variant="contained" 
              color="primary" 
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('create')}
                >
                  Add Product
                </Button>
            </Paper>
          </Grid>
        );
      }
    
    // We're in the ALL tab - show products
    if (filteredProducts.length > 0) {
      const allProductsItems = [];
      
      // Add a section header for products
      allProductsItems.push(
        <Grid item xs={12} key="all-products-header">
          <Box sx={{ mt: 2, mb: 1, display: 'flex', alignItems: 'center' }}>
            <InventoryIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="h2" color="primary.main">
              All Products
            </Typography>
          </Box>
          <Divider sx={{ mb: 2 }} />
        </Grid>
      );
      
      // Add the products in the selected view mode
      if (viewMode === 'grid') {
        filteredProducts.forEach(product => {
          allProductsItems.push(renderProductCard(product));
        });
        return allProductsItems;
      } else {
        allProductsItems.push(
          <Grid item xs={12} key="all-product-list">
            <TableContainer component={Paper} sx={{ mb: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Image</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell align="right">Sale Price</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts.map(product => (
                    <TableRow key={product.id}>
                      <TableCell>
                        <Box sx={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {product.imageUrl ? (
                            <CardMedia
                              component="img" 
                              image={productService.getProductImageUrl(product.imageUrl)}
                              alt={product.name}
                              sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                          ) : (
                            <InventoryIcon color="action" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{product.name}</TableCell>
                      <TableCell>{product.category?.name || 'Uncategorized'}</TableCell>
                      <TableCell align="right">{product.sellingPrice?.toFixed(2) || '0.00'}</TableCell>
                      <TableCell align="right">{product.quantity || 0} {product.unit?.abbreviation || ''}</TableCell>
                      <TableCell align="center">
                        <Chip 
                          label={product.status || 'ACTIVE'} 
                          color={product.status === 'ACTIVE' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <IconButton onClick={(e) => handleMenuOpen(e, product.id)} size="small">
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        );
      }
    }
    
    // No products found
      return (
        <Grid item xs={12}>
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Add products to get started
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Add Product
          </Button>
        </Paper>
        </Grid>
      );
  }, [loading, currentCategory, categories, filteredProducts, viewMode, renderCategoryCard, renderProductCard, handleOpenDialog, handleMenuOpen, productService]);

  // Fetch products based on tab selection or search
  const fetchTabProducts = useCallback(async (categoryId = null) => {
    setLoading(true);
    try {
      let data;
      if (categoryId) {
        console.log(`Fetching products for category ID: ${categoryId}`);
        // Fetch products for selected category
        data = await productService.getProductsByCategoryForManagement(
          categoryId,
          pagination.page,
          pagination.size,
          'name',
          'asc'
        );
      } else {
        console.log('Fetching ALL products');
        // Fetch all products when ALL tab is selected
        try {
          // Try the paginated method first
          data = await productService.getAllProductsForManagement(
            pagination.page,
            pagination.size,
            'name',
            'asc'
          );
          console.log('Got paginated products:', data);
        } catch (err) {
          console.error('Error with paginated products, trying alternative method:', err);
          
          // Fallback: If the paginated method fails, try to get all products without pagination
          try {
            // This assumes there's a method to get all products without pagination
            // or we manually implement a solution to fetch a reasonable number of products
            const allProductsResponse = await axios.get(
              `${API_CONFIG.BASE_URL}/products/all`,
              { headers: getAuthHeader() }
            );
            data = allProductsResponse.data;
            console.log('Got all products with fallback:', data);
          } catch (fallbackErr) {
            console.error('Both product fetch methods failed, using mock data:', fallbackErr);
            // Last resort: Use mock data for demonstration purposes
            data = MOCK_PRODUCTS;
            console.log('Using mock product data:', data);
          }
        }
      }
      
      console.log('Fetched data:', data); // Debug log
      
      // Handle different response structures
      if (data && data.content) {
        setProducts(data.content);
        setFilteredProducts(data.content);
        setPagination(prev => ({
          ...prev,
          totalPages: data.totalPages || 1,
          totalElements: data.totalElements || data.content.length
        }));
      } else if (Array.isArray(data)) {
        setProducts(data);
        setFilteredProducts(data);
        setPagination(prev => ({
          ...prev,
          totalPages: 1,
          totalElements: data.length
        }));
      } else {
        // Fallback for unexpected response format
        console.warn('Unexpected data format:', data);
        setProducts([]);
        setFilteredProducts([]);
        setPagination(prev => ({
          ...prev,
          totalPages: 1,
          totalElements: 0
        }));
      }
    } catch (error) {
      console.error('Error fetching products:', error);
      enqueueSnackbar(`Failed to load products: ${error.message || 'Unknown error'}`, { variant: 'error' });
      
      // Last resort: Use mock data even after error
      console.log('Error occurred, using mock data');
      setProducts(MOCK_PRODUCTS);
      setFilteredProducts(MOCK_PRODUCTS);
      setPagination(prev => ({
        ...prev,
        totalPages: 1,
        totalElements: MOCK_PRODUCTS.length
      }));
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, enqueueSnackbar]);

  // Handle tab change 
  const handleTabChange = (event, newValue) => {
    setActiveTabIndex(newValue);
    
    if (newValue === 0) {
      // "ALL" tab selected - show all products, not categories
      setCurrentCategory(null);
      setBreadcrumbs([{ id: null, name: 'All Products' }]);
      fetchTabProducts(); // This will fetch all products
    } else if (mainCategories.length > 0) {
      // Set current category to the selected tab's category
      const selectedCategory = mainCategories[newValue - 1]; // -1 because "ALL" is index 0
      
      // Set current category regardless of whether it has subcategories
      setCurrentCategory(selectedCategory);
      setBreadcrumbs([
        { id: null, name: 'All Products' },
        { id: selectedCategory.id, name: selectedCategory.name }
      ]);
      
      // Always fetch products for this category
      fetchTabProducts(selectedCategory.id);
    }
    
    // Reset pagination
    setPagination(prev => ({ ...prev, page: 0 }));
  };

  // Load products when a tab is selected or component mounts
  useEffect(() => {
    // Ensure the API is available before trying to fetch
    if (!productService || !productService.getAllProductsForManagement) {
      console.error('Product service or getAllProductsForManagement method not available');
      enqueueSnackbar('Product service unavailable', { variant: 'error' });
      return;
    }
    
    // Initial data load
    if (activeTabIndex === 0) {
      // Load all products
      fetchTabProducts();
    } else if (currentCategory) {
      // Load category products
      fetchTabProducts(currentCategory.id);
    }
  }, [activeTabIndex, currentCategory, fetchTabProducts, enqueueSnackbar]);

  const handleSearch = async (e) => {
    e.preventDefault(); // Prevent default form submission

    // Reset pagination for new search or clear
    setPagination(prev => ({ ...prev, page: 0 }));

    setLoading(true);
    try {
      if (searchQuery.trim()) {
        console.log(`Searching for "${searchQuery}" in category: ${currentCategory?.id || 'ALL'}`);
        // Perform server-side search
        const results = await productService.searchProductsForManagement(searchQuery);

        console.log('Search results:', results); // Debug log

        // Update state with search results
        // Assuming the search service returns a paginated structure { content: [], totalPages: X, ... }
        if (results && results.content) {
          setProducts(results.content); // Update main product list as well? Or keep separate? For now, update both.
          setFilteredProducts(results.content);
          setPagination(prev => ({
            ...prev,
            page: 0, // Reset to page 0
            totalPages: results.totalPages || 1,
            totalElements: results.totalElements || results.content.length
          }));
        } else if (Array.isArray(results)) { // Handle non-paginated results if necessary
            setProducts(results);
            setFilteredProducts(results);
            setPagination(prev => ({
              ...prev,
              page: 0,
              totalPages: 1,
              totalElements: results.length
            }));
        } else {
            // Handle case where search returns nothing or unexpected format
            setProducts([]);
            setFilteredProducts([]);
            setPagination(prev => ({ ...prev, page: 0, totalPages: 0, totalElements: 0 }));
            enqueueSnackbar('No products found matching your search.', { variant: 'info' });
        }

      } else {
        // Clear search - revert to original data for the current view by refetching
        console.log(`Search cleared, fetching products for category: ${currentCategory?.id || 'ALL'}`);
        fetchTabProducts(currentCategory?.id); // Refetch all or category products
      }
    } catch (error) {
      console.error('Error searching:', error);
      enqueueSnackbar(`Search failed: ${error.message || 'Unknown error'}`, { variant: 'error' });
      // Clear results on error
      setProducts([]);
      setFilteredProducts([]);
      setPagination(prev => ({ ...prev, page: 0, totalPages: 0, totalElements: 0 }));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (productId) => {
    const product = products.find(p => p.id === productId);
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const handleDeleteProduct = async () => {
    try {
      if (!productToDelete) return;
      await productService.deleteProduct(productToDelete);
      enqueueSnackbar('Product deleted successfully', { variant: 'success' });
      handleCloseDeleteDialog();
      fetchTabProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      enqueueSnackbar('Failed to delete product', { variant: 'error' });
    }
  };

  // Handle view mode change
  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      console.log('View mode changed to:', newMode); // Debug log
    }
  };

  // Handle category dropdown open
  const handleCategoryDropdownOpen = (event) => {
    setCategoryDropdownAnchorEl(event.currentTarget);
    setCategoryDropdownOpen(true);
  };

  // Handle category dropdown close
  const handleCategoryDropdownClose = () => {
    setCategoryDropdownAnchorEl(null);
    setCategoryDropdownOpen(false);
  };

  // Handle category selection from dropdown
  const handleCategorySelect = (categoryId) => {
    const categoryIndex = mainCategories.findIndex(cat => cat.id === categoryId);
    if (categoryIndex !== -1) {
      handleTabChange(null, categoryIndex + 1); // +1 because ALL is index 0
    }
    handleCategoryDropdownClose();
  };

  // Calculate max visible tabs based on screen width (will be used to determine when to show dropdown)
  const maxVisibleTabs = 8; // Adjust based on testing

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);
      
      // Get the products to export (either filtered or all)
      const productsToExport = products.map(product => ({
        'Product Name': product.name,
        'Category': product.category?.name || 'Uncategorized',
        'Quantity': product.quantity || 0,
        'Selling Price': product.sellingPrice || 0,
        'Purchase Price': product.purchasePrice || 0,
        'Status': product.status || 'ACTIVE',
        'Unit': product.unit || 'PCS',
        'Description': product.description || ''
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(productsToExport);
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Products');
      
      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      // Create download link
      const url = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `products_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      enqueueSnackbar('Products exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting products:', error);
      enqueueSnackbar('Failed to export products', { variant: 'error' });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ px: 3, py: 2 }}>
        {/* Category/Product Navigation Tabs */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row', 
          alignItems: 'center',
          justifyContent: 'space-between', 
          borderBottom: 1, 
          borderColor: 'divider', 
          mb: 3 
        }}>
          <Tabs 
            value={activeTabIndex}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            aria-label="product categories tabs"
            sx={{ 
              flexGrow: 1,
              '& .MuiTabs-scrollButtons': {
                '&.Mui-disabled': { opacity: 0.3 },
                '&:not(.Mui-disabled)': {
                  opacity: 1,
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  borderRadius: '50%'
                }
              }
            }}
          >
            <Tab 
              label="ALL" 
              sx={{ 
                fontWeight: 'medium', 
                fontSize: '0.9rem', 
                minWidth: 100,
                px: 2
              }} 
            />
            {/* Only show a subset of categories in the tabs if there are many */}
            {mainCategories.slice(0, maxVisibleTabs).map((category, index) => (
              <Tab 
                key={category.id}
                label={category.name + (category.code ? ' ' + category.code : '')} 
                sx={{ 
                  fontWeight: 'medium', 
                  fontSize: '0.9rem',
                  minWidth: 100,
                  px: 2
                }} 
              />
            ))}
          </Tabs>
          
          {/* Show "More Categories" dropdown if we have more categories than can fit in tabs */}
          {mainCategories.length > maxVisibleTabs && (
            <Button
              variant="outlined" 
              color="primary"
              endIcon={<ExpandMoreIcon />}
              onClick={handleCategoryDropdownOpen}
              size="small"
              sx={{ ml: 1, flexShrink: 0 }}
            >
              More Categories
            </Button>
          )}
          
          {/* Dropdown menu for additional categories */}
          <Menu
            anchorEl={categoryDropdownAnchorEl}
            open={categoryDropdownOpen}
            onClose={handleCategoryDropdownClose}
            sx={{ maxHeight: 300 }}
          >
            {mainCategories.slice(maxVisibleTabs).map((category) => (
              <MenuItem 
                key={category.id} 
                onClick={() => handleCategorySelect(category.id)}
                selected={activeTabIndex === mainCategories.findIndex(cat => cat.id === category.id) + 1}
              >
                <CategoryIcon sx={{ mr: 1, fontSize: '1rem' }} />
                {category.name}
                {category.code && <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{category.code}</Typography>}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Products Header */}
        <Container maxWidth="xl" sx={{ mb: 3, mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Breadcrumbs navigation */}
              <Breadcrumbs 
                separator={<ChevronRightIcon fontSize="small" />} 
                aria-label="breadcrumb"
                sx={{ mr: 2 }}
              >
                {breadcrumbs.map((crumb, index) => (
                  <Link
                    key={index}
                    color={index === breadcrumbs.length - 1 ? 'text.primary' : 'inherit'}
                    sx={{ 
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontWeight: index === breadcrumbs.length - 1 ? 'medium' : 'regular'
                    }}
                    onClick={() => handleBreadcrumbClick(crumb, index)}
                    underline="hover"
                  >
                    {index === 0 && <CategoryIcon sx={{ mr: 0.5, fontSize: '1rem' }} />}
                    {crumb.name}
                  </Link>
                ))}
              </Breadcrumbs>
              
              {/* Back button - only show when viewing subcategories */}
              {currentCategory && breadcrumbs.length > 2 && (
                <Button
                variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  size="small"
                  onClick={() => handleBreadcrumbClick(breadcrumbs[breadcrumbs.length - 2], breadcrumbs.length - 2)}
                  sx={{ mr: 2 }}
                >
                  Back
                </Button>
              )}
              
              <Typography 
                variant="h4" 
                component="h1"
                sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  color: 'text.primary'
                }}
              >
                {currentCategory ? currentCategory.name : 'Products'}
              </Typography>
          </Box>
          
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Box 
                component="form" 
                onSubmit={handleSearch}
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <TextField
                  size="small"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch(e);
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  variant="outlined"
                />
                <Button 
                  type="submit" 
                  variant="contained" 
                  size="medium"
                  disabled={loading}
                  onClick={handleSearch}
                >
                  Search
                </Button>
              </Box>
              <Button
                variant="outlined"
                onClick={handleCategoryDropdownOpen}
                endIcon={<KeyboardArrowDownIcon />}
                sx={{ minWidth: '150px' }}
              >
                {currentCategory ? currentCategory.name : 'All Categories'}
              </Button>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog('create')}
            >
              Add Product
            </Button>
              <Button
                variant="outlined"
                startIcon={isExporting ? <CircularProgress size={20} /> : <FileDownloadIcon />}
                onClick={handleExportToExcel}
                disabled={isExporting || products.length === 0}
                sx={{ 
                  minWidth: '120px',
                  backgroundColor: 'white',
                  '&:hover': {
                    backgroundColor: 'grey.50'
                  }
                }}
              >
                {isExporting ? 'Exporting...' : 'Export Excel'}
              </Button>
            <ToggleButtonGroup
              value={viewMode}
              exclusive
              onChange={handleViewModeChange}
              aria-label="view mode"
              size="small"
              sx={{ 
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                '& .MuiToggleButton-root.Mui-selected': {
                  backgroundColor: 'primary.light',
                  color: 'white'
                }
              }}
            >
              <ToggleButton value="grid" aria-label="grid view">
                <GridViewIcon />
              </ToggleButton>
              <ToggleButton value="list" aria-label="list view">
                <ListViewIcon />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>
        </Box>
        </Container>
        
        {/* Products Display */}
        <Grid container spacing={2}>
          {contentGrid}
        </Grid>

        {/* Pagination */}
        {filteredProducts.length > 0 && pagination.totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 2 }}>
            <Pagination 
              count={pagination.totalPages} 
              page={pagination.page + 1} // Convert 0-based to 1-based for MUI Pagination
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              disabled={loading}
            />
          </Box>
        )}

        {/* Product Creation/Editing Dialog */}
        <Dialog
          open={openDialog} 
          onClose={handleCloseDialog}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent sx={{ p: 0 }}>
            <ProductForm
              initialData={currentProduct}
              onSubmit={handleProductSubmit}
              onCancel={handleCloseDialog}
              isViewMode={dialogMode === 'view'}
            />
          </DialogContent>
          {/* Add Close button for view mode */}
          {dialogMode === 'view' && (
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button 
                variant="contained" 
                onClick={handleCloseDialog}
              >
                Close
              </Button>
            </DialogActions>
          )}
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteDialog}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this product? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
            <Button 
              onClick={handleDeleteProduct} 
              variant="contained" 
              color="error"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Menu for product actions */}
        <Menu
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            handleOpenDialog('view', products.find(p => p.id === selectedProductId));
            handleMenuClose();
          }}>
            <InventoryIcon sx={{ mr: 1 }} fontSize="small" />
            View Details
          </MenuItem>
          <MenuItem onClick={() => {
            handleOpenDialog('edit', products.find(p => p.id === selectedProductId));
            handleMenuClose();
          }}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
          <MenuItem onClick={() => {
            handleDeleteClick(selectedProductId);
            handleMenuClose();
          }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        </Menu>
      </Box>
    </Layout>
  );
};

export default ProductsPage; 